<template>
  <v-sheet>
    <b-card
      :title="translations.redeemCouponsDialogTitle[lang]"
      @cancel="$emit('close')"
      @submit="submitRedeemCoupons"
      :disabled="!formValid"
      :loading="loading"
      :submit-text="translations.submit"
      :cancel-text="translations.cancel"
    >
      <v-row no-gutters>
        <v-col cols="12">
          <v-autocomplete
            v-model="redeemCouponsData.coupon_id"
            :items="couponsData"
            item-text="title"
            item-value="coupon_id"
            menu-props="offsetY"
            :label="translations.selectCoupon[lang]"
            class="mt-4"
            outlined
            dense
            hide-details
            clearable
            :search-input.sync="searchCoupons"
            :loading="loadingCoupons"
            :disable-lookup="true"
          >
            <template v-slot:item="{ item }">
              <v-col class="ma-0 pa-0" style="max-width: 500px">
                <v-tooltip v-if="!canRedeemCoupon(item) || !item.show_on_catalog" top>
                  <template v-slot:activator="{ on, attrs }">
                    <div
                      v-bind="attrs"
                      v-on="on"
                      class="d-flex align-center"
                      style="cursor: not-allowed; opacity: 0.5"
                    >
                      <v-avatar size="32" class="mr-3">
                        <img v-if="item.image" :src="item.image" alt="coupon" />
                        <v-icon v-else small>mdi-tag</v-icon>
                      </v-avatar>
                      <div>
                        <div>
                          {{ item.title }}
                        </div>
                        <div class="caption grey--text">ID: {{ item.coupon_id }}</div>
                      </div>
                    </div>
                  </template>
                  <span v-if="!item.show_on_catalog">{{ translations.notVisible[lang] }}</span>
                  <span v-else
                    >{{ translations[`notEnough_${storeMode}`][lang] }} ({{ item.points }})</span
                  >
                </v-tooltip>
                <div v-else class="d-flex align-center">
                  <v-avatar size="32" class="mr-3">
                    <img v-if="item.image" :src="item.image" alt="coupon" />
                    <v-icon v-else small>mdi-tag</v-icon>
                  </v-avatar>
                  <div>
                    <div>{{ item.title }}</div>
                    <div class="caption grey--text">ID: {{ item.coupon_id }}</div>
                  </div>
                </div>
              </v-col>
            </template>
            <template v-slot:append-item>
              <div v-intersect="endIntersectCoupons"></div>
              <v-list-item v-if="loadingCoupons">
                <v-list-item-action class="ml-1">
                  <v-row class="d-flex align-center">
                    <v-btn
                      class="text-capitalize"
                      depressed
                      fab
                      small
                      :loading="true"
                      style="background: transparent"
                    >
                    </v-btn>
                    <span class="pa-2">{{ translations.loadMore[lang] }}</span>
                  </v-row>
                </v-list-item-action>
              </v-list-item>
            </template>
          </v-autocomplete>
        </v-col>
        <v-col cols="12">
          <b-text-field
            v-model.number="redeemCouponsData.quantity"
            type="number"
            :label="translations.quantity[lang]"
            :error-messages="error.quantity"
            :success="success.quantity"
            @blur="validateQuantity"
            @input="validatePoints"
          />
        </v-col>
        <v-col v-if="selectedCoupon && !selectedCoupon.isProduct" cols="12" sm="12">
          <v-autocomplete
            :items="storesData.stores"
            item-text="displayName"
            item-value="store_id"
            v-model="redeemCouponsData.store_id"
            :label="translations.store[lang]"
            outlined
            dense
            clearable
            :error-messages="error.store_id"
            :success="success.store_id"
            color="black"
            menu-props="offsetY"
            item-color="secondary"
            open-on-clear
            hide-details="auto"
            class="mt-3"
            :loading="companyStoresLoading"
            validate-on-blur
            :search-input.sync="storesSearch"
          >
            <template v-slot:append-item>
              <div
                v-intersect="{
                  handler: (entries, observer) => endIntersectStores(entries, observer),
                }"
              ></div>
            </template>
          </v-autocomplete>
        </v-col>
        <v-col v-if="userPoints.length" cols="12" class="my-4 px-2 mt-3">
          <v-simple-table>
            <thead>
              <tr>
                <th>{{ translations.loyaltyProgram[lang] }}</th>
                <th class="text-right">{{ translations.pointBalance[lang] }}</th>
                <th class="text-right">{{ translations.couponCost[lang] }}</th>
              </tr>
            </thead>
            <tbody>
              <!-- Existing user points -->
              <tr v-for="point in userPoints" :key="point.loyalty_program_id">
                <td>{{ point.name[lang] }}</td>
                <td class="text-right">{{ point.points }}</td>
                <td
                  class="text-right"
                  :style="{
                    color:
                      calculateCouponCost(point.loyalty_program_id) > point.points
                        ? 'red'
                        : 'inherit',
                  }"
                >
                  <span v-if="selectedCoupon">{{
                    calculateCouponCost(point.loyalty_program_id)
                  }}</span>
                  <span v-else>-</span>
                </td>
              </tr>
              <tr
                v-for="loyaltyProgramId in missingLoyaltyPrograms"
                :key="'missing-' + loyaltyProgramId"
              >
                <td>{{ translations.missingLoyaltyProgram[lang] }}</td>
                <td class="text-right">0</td>
                <td class="text-right" style="color: red">
                  {{ calculateCouponCost(loyaltyProgramId) }}
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-col>
        <v-col cols="12" v-if="selectedCoupon && selectedCoupon.isProduct">
          <v-row>
            <v-col md="6">
              <b-text-field
                v-model="redeemCouponsData.shipping_details.recipient_information.first_name"
                :label="translations.firstName[lang]"
                :error-messages="error.first_name"
                :success="success.first_name"
                @blur="validateFirstName"
              />
            </v-col>
            <v-col md="6">
              <b-text-field
                v-model="redeemCouponsData.shipping_details.recipient_information.last_name"
                :label="translations.lastName[lang]"
                :error-messages="error.last_name"
                :success="success.last_name"
                @blur="validateLastName"
              />
            </v-col>
          </v-row>
          <b-text-field
            v-model="redeemCouponsData.shipping_details.recipient_information.mobile"
            :label="translations.mobile[lang]"
            :error-messages="error.mobile"
            :success="success.mobile"
            @blur="validateMobile"
            @change="validateMobile"
          />
          <b-text-field
            v-model="redeemCouponsData.shipping_details.recipient_information.email"
            :label="translations.email[lang]"
            :error-messages="error.email"
            :success="success.email"
            @blur="validateEmail"
            @change="validateEmail"
          />
          <b-text-field
            v-model="redeemCouponsData.shipping_details.recipient_address.address"
            :label="translations.address[lang]"
            :error-messages="error.address"
            :success="success.address"
            @blur="validateAddress"
          />
          <v-row>
            <v-col md="6">
              <b-text-field
                v-model="redeemCouponsData.shipping_details.recipient_address.postal_code"
                :label="translations.postalCode[lang]"
                :error-messages="error.postal_code"
                :success="success.postal_code"
                @blur="validatePostalCode"
              />
            </v-col>
            <v-col md="6">
              <b-text-field
                v-model="redeemCouponsData.shipping_details.recipient_address.city"
                :label="translations.city[lang]"
                :error-messages="error.city"
                :success="success.city"
                @blur="validateCity"
              />
            </v-col>
          </v-row>
          <v-row class="mt-0">
            <v-col md="8" class="pt-0">
              <b-text-field
                v-model="redeemCouponsData.shipping_details.recipient_address.bell_name"
                :label="translations.bellName[lang]"
                :error-messages="error.bell_name"
                :success="success.bell_name"
                @blur="validateBellName"
              />
            </v-col>
            <v-col md="4" class="pt-0">
              <b-text-field
                v-model="redeemCouponsData.shipping_details.recipient_address.floor"
                :label="translations.floor[lang]"
                :error-messages="error.floor"
                :success="success.floor"
                @blur="validateFloor"
              />
            </v-col>
          </v-row>
          <b-textarea
            v-model="redeemCouponsData.shipping_details.recipient_address.notes"
            :label="translations.notes[lang]"
          />
        </v-col>
      </v-row>
    </b-card>
  </v-sheet>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex';
import translations from '@/utils/translations/loyaltyPanel/customers';
import validators from '@/utils/validators/loyaltyPanel/customersCouponsRedeem';
import { debounce, cloneDeep } from 'lodash';

export default {
  name: 'CustomerCouponsRedeem',
  mixins: [validators, translations],
  props: {
    customer: {
      type: Object,
      required: false,
      default: () => ({ user_account_id: null }),
    },
  },
  data() {
    return {
      redeemCouponsData: {
        user_account_id: null,
        coupon_id: null,
        store_id: null,
        shipping_details: {
          recipient_address: {
            city: '',
            address: '',
            postal_code: '',
            floor: '',
            notes: '',
            bell_name: '',
          },
          recipient_information: {
            first_name: '',
            last_name: '',
            mobile: '',
            email: '',
          },
        },
        quantity: 1,
      },
      couponsData: [],
      storesData: {
        stores: [],
        pagination: {
          current_page: 1,
          next_page: null,
          total: 0,
        },
      },
      storesSearch: '',
      searchCoupons: '',
      searchStores: '',
      loadingCoupons: false,
      loadingStores: false,
      couponPage: 1,
    };
  },
  computed: {
    ...mapState(['loading']),
    ...mapState('loyaltyPanel', ['company', 'companyStores', 'companyStoresLoading']),
    ...mapState('loyaltyPanel/customers', ['customers', 'userPoints']),
    ...mapState('loyaltyPanel/catalogManagement', ['gifts', 'giftsPagination']),
    lang() {
      return this.$route.params.lang;
    },
    storeMode() {
      return this.$store.state.store?.display_mode || 'points';
    },
    selectedCoupon() {
      let selectedItem = this.couponsData.find(
        (coupon) => coupon.coupon_id === this.redeemCouponsData.coupon_id
      );
      if (!selectedItem) {
        return null;
      }
      selectedItem.isProduct =
        selectedItem?.gift_type?.gift_type_id === 1 ||
        selectedItem?.gift_type?.name?.en?.toLowerCase() === 'product';
      return selectedItem;
    },
    missingLoyaltyPrograms() {
      if (!this.selectedCoupon || !this.selectedCoupon.loyalty_program_ids) {
        return [];
      }

      return this.selectedCoupon.loyalty_program_ids.filter(
        (loyaltyProgramId) =>
          !this.userPoints.find((point) => point.loyalty_program_id === loyaltyProgramId)
      );
    },
  },
  methods: {
    ...mapMutations(['setNotification']),
    ...mapActions('loyaltyPanel', ['loadStores']),
    ...mapActions('loyaltyPanel/catalogManagement', ['getGifts']),
    ...mapActions('loyaltyPanel/customers', ['redeemCoupons']),

    resetRedeemCoupons() {
      this.redeemCouponsData = {
        user_account_id: null,
        coupon_id: null,
        store_id: null,
        shipping_details: {
          recipient_address: {
            city: '',
            address: '',
            postal_code: '',
            floor: '',
            notes: '',
            bell_name: '',
          },
          recipient_information: {
            first_name: '',
            last_name: '',
            mobile: '',
            email: '',
          },
        },
        quantity: 1,
        isGiftCard: false,
        isProduct: false,
      };
    },
    async submitRedeemCoupons() {
      const body = {
        user_account_id: this.redeemCouponsData.user_account_id,
        coupon_id: this.redeemCouponsData.coupon_id,
        quantity: this.redeemCouponsData.quantity,
        store_id: this.redeemCouponsData.store_id,
      };
      if (this.selectedCoupon?.isProduct) {
        body.shipping_details = {
          recipient_address: this.redeemCouponsData.shipping_details.recipient_address,
          recipient_information: this.redeemCouponsData.shipping_details.recipient_information,
        };
      }
      const res = await this.redeemCoupons(body);
      if (res && !res.error) {
        this.setNotification({
          show: true,
          type: 'success',
          text: this.translations.redeemCouponsSuccess[this.lang],
        });
        this.$emit('close');
      } else {
        this.setNotification({
          show: true,
          type: 'error',
          text: res?.message || this.translations.error[this.lang],
        });
      }
    },
    async handleSearchCoupons() {
      let query = `?page=${this.couponPage}`;
      if (this.searchCoupons) {
        query += `&q=${this.searchCoupons}`;
      }
      await this.getGifts(query);
      if (this.couponPage === 1) {
        this.couponsData = this.gifts;
      } else {
        this.couponsData = [...this.couponsData, ...this.gifts];
      }
    },
    endIntersectCoupons(entries, observer, isIntersecting) {
      if (isIntersecting) {
        this.loadMoreCoupons();
      }
    },
    async loadMoreCoupons() {
      if (this.giftsPagination?.next_page) {
        this.couponPage = this.giftsPagination.next_page;
        await this.debouncedSearchCoupons();
      }
    },

    handleCouponSelection() {
      this.validatePoints();
    },

    calculateCouponCost(loyaltyProgramId) {
      const selectedCoupon = this.selectedCoupon;

      if (!selectedCoupon) {
        return 0;
      }

      const costPerQuantity = selectedCoupon.points * this.redeemCouponsData.quantity;

      if (this.company.loyalty_program_id === loyaltyProgramId) {
        return costPerQuantity;
      }

      if (selectedCoupon.loyalty_program_ids.includes(loyaltyProgramId)) {
        return costPerQuantity;
      }

      return 0;
    },

    canRedeemCoupon(coupon) {
      if (!coupon) {
        return false;
      }

      const totalPointsRequired = coupon.points * this.redeemCouponsData.quantity;

      const mainLoyaltyProgram = this.userPoints.find(
        (point) => point.loyalty_program_id === this.company.loyalty_program_id
      );

      if (!mainLoyaltyProgram || mainLoyaltyProgram.points < totalPointsRequired) {
        return false;
      }

      return coupon.loyalty_program_ids.every((loyaltyProgramId) => {
        const userPoints = this.userPoints.find(
          (point) => point.loyalty_program_id === loyaltyProgramId
        );

        const availablePoints = userPoints ? userPoints.points : 0;
        return availablePoints >= totalPointsRequired;
      });
    },
    async handleSearchStores() {
      this.storesData.pagination.current_page = 1;
      this.storesData.pagination.next_page = null;
      await this.getStores();
    },
    updateStoreData(data) {
      this.storesData.pagination = cloneDeep(data.pagination) || {};

      const mappedStores = (data.stores || []).map((s) => ({
        ...s,
        displayName: s.store_code ? `${s.store_code} ${s.app_name}` : s.app_name,
      }));

      if (data.pagination?.current_page > 1) {
        this.storesData.stores = [...this.storesData.stores, ...mappedStores];
      } else if (data.pagination?.current_page === 1) {
        this.storesData.stores = mappedStores;
      }
    },

    endIntersectStores(entries, observer) {
      if (this.storesData?.stores?.length < this.storesData?.pagination.total) {
        this.loadMoreStores();
      }
    },

    getStoresQuery(data) {
      let q = `?page=${data.page}`;
      if (this.storesSearch) {
        q += `&q=${this.storesSearch}`;
      }
      return q;
    },

    async getStores() {
      let res = await this.loadStores({
        query: this.getStoresQuery({
          page: this.storesData.pagination.current_page,
        }),
        page: this.storesData.pagination.current_page,
      });
      this.updateStoreData(res);
    },

    async loadMoreStores() {
      if (this.storesData.pagination?.next_page) {
        this.storesData.pagination.current_page = this.storesData.pagination.next_page;
        await this.getStores();
      }
    },
  },
  watch: {
    'customer.user_account_id': {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.resetRedeemCoupons();
          this.redeemCouponsData.user_account_id = newVal;
        }
      },
    },
    searchCoupons: {
      handler(newVal, oldVal) {
        this.couponPage = 1;
        this.debouncedSearchCoupons();
      },
    },
    selectedCoupon: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (!newVal) {
          return;
        }
        if (newVal.isProduct) {
          if (newVal.isProduct) {
            this.redeemCouponsData.store_id = null;
          } else {
            this.getStores();
            this.redeemCouponsData.shipping_details = {
              recipient_address: {
                city: '',
                address: '',
                postal_code: '',
                floor: '',
                notes: '',
                bell_name: '',
              },
              recipient_information: {
                first_name: '',
                last_name: '',
                mobile: '',
                email: '',
              },
            };
          }
        }
      },
    },

    storesSearch: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.debouncedSearchStores();
        }
      },
    },
  },
  async mounted() {
    this.handleSearchCoupons();
  },
  created() {
    this.debouncedSearchCoupons = debounce(this.handleSearchCoupons, 800);
    this.debouncedSearchStores = debounce(this.handleSearchStores, 800);
  },
};
</script>
